import { useSnackbar } from 'notistack'
import { FC, useCallback, useContext, useEffect, useState } from 'react'
import { useIntl } from 'react-intl'
import { useQueryClient } from 'react-query'
import { useLocation, useNavigate } from 'react-router-dom'
import { useTggl } from 'react-tggl-client'
import { routeNames } from 'src/modules/core/constants/routeNames'
import { LoadingWrapperContext } from 'src/modules/core/context'
import { AuthContext } from 'src/modules/user/context'
import { GET_BOOKING_ONBOARDING_CACHE_KEY } from 'src/modules/user/hooks/use-booking-onboarding'
import { GET_PROFILE_CACHE_KEY } from 'src/modules/user/hooks/use-profile'
import { ApiService } from 'src/services/api.service'
import { BookingOnboardingService } from 'src/services/booking-onboarding.service'
import { TokenService } from 'src/services/token.service'

export const LoginWithToken: FC = () => {
  const [hasStarted, setHasStarted] = useState(false)
  const { enqueueSnackbar } = useSnackbar()
  const { formatMessage } = useIntl()
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const { updateContext: updateTgglContext } = useTggl()
  const { authenticateUser, logoutUser } = useContext(AuthContext)
  const { enableLoading, disableLoading, setLoadingMessage } = useContext(LoadingWrapperContext)
  const location = useLocation()

  const handleLoginFailure = useCallback(() => {
    disableLoading()
    logoutUser()
    enqueueSnackbar(formatMessage({ id: 'login.error.unknown' }), { variant: 'error' })
    navigate(routeNames.login.path)
  }, [logoutUser, disableLoading, enqueueSnackbar, formatMessage, navigate])

  const handleTokenSuccess = useCallback(
    async (accessToken: string) => {
      TokenService.getInstance().setToken(accessToken)
      authenticateUser()

      // Check if this is a booking user and create booking user if needed
      const searchParams = new URLSearchParams(location.search)
      const isBookingUser = searchParams.get('isBookingUser') === 'true'

      if (isBookingUser) {
        try {
          await ApiService.Profile.createBookingUser()
          // Invalidate and immediately refetch profile cache after booking user creation
          // This ensures we get the updated profile with completed_onboarding flag
          // and updated message template timestamps
          await queryClient.invalidateQueries([GET_PROFILE_CACHE_KEY])
          await queryClient.refetchQueries([GET_PROFILE_CACHE_KEY])
        } catch (error) {
          // Log error but continue with authentication flow
          // The endpoint is idempotent and handles existing users gracefully
          console.error('Failed to create booking user:', error)
        }
      }

      const params = {}
      const profile = await queryClient.fetchQuery(
        [GET_PROFILE_CACHE_KEY, params],
        () => ApiService.Profile.getProfile(params),
        { staleTime: 300000 },
      )

      if (!profile) {
        handleLoginFailure()
        return
      }

      updateTgglContext({ userId: profile.id, email: profile.email })

      // Check if user is a booking user and needs to complete booking onboarding
      if (profile.is_booking_user) {
        try {
          const bookingOnboarding = await BookingOnboardingService.get()
          if (bookingOnboarding && !bookingOnboarding.onboarding_completed) {
            // Invalidate booking onboarding cache to ensure fresh data
            await queryClient.invalidateQueries([GET_BOOKING_ONBOARDING_CACHE_KEY])
            navigate(routeNames.bookingOnboarding.path)
            return
          }
        } catch (error) {
          // If booking onboarding fetch fails, continue with normal redirect
          console.error('Failed to fetch booking onboarding:', error)
        }
      }

      const redirectUrl = searchParams.get('redirect') || routeNames.dashboard.path
      navigate(redirectUrl)
    },
    [authenticateUser, queryClient, updateTgglContext, handleLoginFailure, navigate, location.search],
  )

  const checkTokenAndLogin = useCallback(
    async (encodedToken: string | null) => {
      enableLoading()
      setLoadingMessage('Authenticating...')
      if (!encodedToken) {
        handleLoginFailure()
        return
      }

      try {
        const token = atob(encodedToken)
        await handleTokenSuccess(token)
      } catch (error) {
        handleLoginFailure()
      }
    },
    [enableLoading, setLoadingMessage, handleTokenSuccess, handleLoginFailure],
  )

  useEffect(() => {
    if (hasStarted) return

    setHasStarted(true) // Set guard to true to prevent re-execution
    const params = new URLSearchParams(location.search)
    const encodedToken = params.get('token')
    if (encodedToken) checkTokenAndLogin(encodedToken)
    else handleLoginFailure()
  }, [hasStarted, checkTokenAndLogin, handleLoginFailure, location.search])

  return null
}
